/**
 * Validation for `SettingType === "time"` values (PRET-1836 / PRD v0.4 FR-10a).
 *
 * A time setting is a "HH:mm" wall-clock string in the ACCOUNT's time zone — the check-in window is
 * the first user. This lives apart from AccountSettingModal so it can be tested directly: the modal
 * is a large MUI form and the rule that actually matters is this one.
 *
 * Why validate in the back office at all, given the backend also parses it: the backend's parse
 * FALLS BACK to the 09:00-19:00 default when it fails, and does not reject the write. So an
 * unvalidated "7pm" saves successfully, displays as configured, and governs nothing — the worst
 * shape of failure, because the screen disagrees with the behaviour. Catching it here is what keeps
 * the two honest.
 */

/** 24-hour HH:mm, 00:00 through 23:59. Deliberately strict — no "9:00", no "7pm", no seconds. */
const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const SETTING_TIME_FORMAT_HINT = "Enter a time as HH:mm, e.g. 09:00";

export function isValidSettingTime(value: unknown): value is string {
  return typeof value === "string" && HH_MM.test(value.trim());
}

/**
 * Normalise a time value for saving.
 *
 * Returns the trimmed value when valid, `""` for an intentionally empty field (clearing a setting is
 * legitimate — the backend then applies its default), and `null` when the value is present but
 * unparseable, which the caller must surface as a validation error rather than save.
 */
export function normaliseSettingTime(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return "";
  return HH_MM.test(trimmed) ? trimmed : null;
}

/**
 * Is `end` strictly after `start`?
 *
 * PRET-1981 AC7 / TestRail C16681: an inverted or zero-width window must be rejected explicitly. The
 * backend also guards this (it falls back to the default), but the same reasoning as above applies —
 * silently ignoring what someone typed is worse than telling them it is wrong. Note the Hi-Fi design
 * does not draw this error state; the copy here is provisional pending that design answer.
 */
export function isWindowOrdered(start: unknown, end: unknown): boolean {
  if (!isValidSettingTime(start) || !isValidSettingTime(end)) return false;
  const toMinutes = (v: string): number => {
    const [h, m] = v.trim().split(":");
    return Number(h) * 60 + Number(m);
  };
  return toMinutes(end) > toMinutes(start);
}
